import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import i18n from "./i18n";
import { installChunkReloadHandler } from "./lib/chunkReload";
import "./index.css";

// Optional analytics loader — only injects if env variables are configured
const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
const analyticsWebsiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;

if (typeof document !== "undefined" && analyticsEndpoint && analyticsWebsiteId) {
  const script = document.createElement("script");
  script.defer = true;
  script.src = `${analyticsEndpoint.replace(/\/$/, "")}/umami`;
  script.dataset.websiteId = analyticsWebsiteId;
  document.body.appendChild(script);
}

// Выкатили новую версию — открытые вкладки обновляем сами, без экрана ошибки.
installChunkReloadHandler();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Меньше «штормов» запросов: не перезапрашивать всё при возврате на вкладку,
      // не плодить повторы при сбоях сети (было до 3 — раздувало нагрузку при обрыве),
      // и небольшой staleTime, чтобы частые перерисовки не дёргали дубль-запросы.
      // Конкретные запросы со своим staleTime/refetchInterval это переопределяют.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000,
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const safeSuperjson = {
  serialize: superjson.serialize,
  deserialize: (data: unknown) => {
    if (data && typeof data === "object" && "json" in data) {
      const payload = data as { json: unknown; meta?: unknown | null };
      if (payload.meta == null) {
        return payload.json;
      }
    }
    return superjson.deserialize(data as any);
  },
};

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: safeSuperjson,
      // Текущий язык интерфейса → сервер локализует ошибки/письма под него.
      headers() {
        return { "x-locale": i18n.resolvedLanguage || i18n.language || "ru" };
      },
      async fetch(input, init) {
        const response = await globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
        const contentType = response.headers.get("content-type") || "";
        if (!response.ok || !contentType.includes("application/json")) {
          try {
            const bodyText = await response.clone().text();
            console.error("[tRPC Response]", response.status, contentType, bodyText);
          } catch (err) {
            console.error("[tRPC Response] Failed to read body:", err);
          }
        }
        return response;
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
